import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ActiveSubsectionState,
  FileTreeNode,
  GetVersionFilesParams,
  GridOption,
  MalwareScanningStatus,
  MoveSubsectionFilesParams,
  OptionInfo,
  OptionVersion,
  ProjectSubSectionUIState,
  QueryState,
  SingleItemQueryState,
  SubsectionNavigationParams,
  VersionCreationParams,
  TopicInfo,
  TopicUpdateDto,
  TopicCreationResponse,
  OptionThumbnail,
  FileInfo,
  FilePages,
  OptionVersionSimple,
  ReviewLinkNavigationParams,
  SubsectionUrlConfig,
  OptionUpdateResponse,
} from '../interfaces';
import { tap } from 'rxjs/operators';
import { Params, Router } from '@angular/router';
import { TreeSortMode } from '../models';
import { ProjectService } from './project.service';
import { MtmContextService } from '@mtm/context-ui-angular';
import { VIEW_CONTEXT_KEY } from './view-context-keys';

@Injectable({
  providedIn: 'root',
})
export class SubsectionService {
  subsectionState: ActiveSubsectionState = null;
  subsection$: BehaviorSubject<ActiveSubsectionState> = new BehaviorSubject<ActiveSubsectionState>(
    this.subsectionState
  );
  private hasInitializedCommentsPanelState = false;
  uiState: ProjectSubSectionUIState = {
    explorerCollapsed: false,
    commentsCollapsed: false,
    cinemaMode: false,
    compareMode: false,
    compareViewMode: undefined,
    orderBy: TreeSortMode.alphabetical,
    isNavigationAffected: true,
    isShareLinkPreview: false,
    isReleasePreview: false,
    rightPanelView: 'comment',
  };
  uiState$: BehaviorSubject<ProjectSubSectionUIState> = new BehaviorSubject<ProjectSubSectionUIState>(this.uiState);
  topics$: BehaviorSubject<QueryState<TopicInfo>> = new BehaviorSubject<QueryState<TopicInfo>>({
    data: [],
    isLoading: false,
    error: null,
  });
  options$: BehaviorSubject<QueryState<OptionInfo>> = new BehaviorSubject<QueryState<OptionInfo>>({
    data: [],
    isLoading: false,
    error: null,
  });
  kpfTree$: BehaviorSubject<SingleItemQueryState<FileTreeNode>> = new BehaviorSubject<
    SingleItemQueryState<FileTreeNode>
  >({
    data: null,
    isLoading: false,
    error: null,
  });
  commentCount$: BehaviorSubject<Record<string, number>> = new BehaviorSubject<Record<string, number>>({});
  private lastSubsectionParams: Record<string, SubsectionNavigationParams> = {};

  private readonly projectService = inject(ProjectService);
  private readonly viewContext = inject(MtmContextService);

  constructor(
    private readonly apiService: ApiService,
    private readonly router: Router
  ) {}

  private isDriveMode(): boolean {
    return this.projectService.project?.mode === 'drive';
  }

  public createNewVersion(params: VersionCreationParams): Observable<OptionInfo> {
    const url: string = `/api/versioning/options/new-version`;
    return this.apiService.httpPost(url, params);
  }

  public createNextVersion(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string
  ): Observable<OptionVersion> {
    const url: string = `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/createNextVersion/${itemId}`;
    return this.apiService.httpPost(url, {});
  }

  public deleteVersions(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    versions: number[]
  ): Observable<void> {
    const url: string = `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/deleteVersion/${itemId}`;
    return this.apiService.httpDelete(url, versions);
  }

  public reorderVersions(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    versions: number[]
  ): Observable<OptionVersion[]> {
    const url: string = `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/reOrderVersion/${itemId}`;
    return this.apiService.httpPost(url, versions);
  }

  public reorderVersionFiles(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    version: number,
    fileOrder: Record<number, string>
  ): Observable<OptionVersion> {
    const url: string = `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/reOrderVersionFiles/${itemId}/${version}`;
    return this.apiService.httpPut(url, fileOrder);
  }

  public deleteFile(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    itemId: string,
    fileId: string,
    version: number = 0
  ): Observable<void> {
    let deleteUrl: string =
      `/api/projects/${projectId}/sections/${sectionId}/subsections/${subsectionId}/items/` +
      `${itemId}/files/${fileId}`;
    if (version > 0) {
      deleteUrl += `?versionNumber=${version}`;
    }
    return this.apiService.httpDelete(deleteUrl);
  }

  public moveFiles(params: MoveSubsectionFilesParams): Observable<void> {
    const url: string = `/api/versioning/options/move-file`;
    return this.apiService.httpPost(url, params);
  }

  //#region topic functions

  public getTopics(projectId: string, sectionId: string, subsectionId: string): Observable<TopicInfo[]> {
    const url: string = `/api/versioning/topics/?project=${projectId}&section=${sectionId}&subsection=${subsectionId}`;
    return this.apiService.httpGet(url);
  }

  public createTopic(info: TopicUpdateDto): Observable<TopicCreationResponse> {
    let url: string = `/api/versioning/topics/?project=${info.projectId}&section=${info.sectionId}&subsection=${info.subsectionId}`;
    if (info.title) {
      url += '&topic=' + encodeURIComponent(info.title);
    }
    if (info.description) {
      url += '&description=' + encodeURIComponent(info.description);
    }
    return this.apiService.httpPost(url, {});
  }

  public updateTopic(info: TopicUpdateDto): Observable<TopicInfo> {
    let url: string = `/api/versioning/topics/?project=${info.projectId}&section=${info.sectionId}&subsection=${info.subsectionId}&topicId=${info.topicId}`;
    if (info.title) {
      url += '&topic=' + encodeURIComponent(info.title);
    }
    if (info.description) {
      url += '&description=' + encodeURIComponent(info.description);
    }

    return this.apiService.httpPut(url, {});
  }

  public deleteTopic(projectId: string, sectionId: string, subsectionId: string, topicId: string): Observable<void> {
    const url: string = `/api/versioning/topics/?project=${projectId}&section=${sectionId}&subsection=${subsectionId}&topicId=${topicId}`;
    return this.apiService.httpDelete(url);
  }

  //#endregion

  //#region option functions

  public getOptions(projectId: string, sectionId: string, subsectionId: string): Observable<OptionInfo[]> {
    const url: string = `/api/versioning/options?project=${projectId}&section=${sectionId}&subsection=${subsectionId}`;
    return this.apiService.httpGet(url).pipe(
      tap((data: OptionInfo[]) => {
        data.forEach((option: OptionInfo) => {
          if (!option.title) option.title = '';
          if (option?.optionId) {
            option.id = option.optionId;
          }
          if (option.simpleVersions) {
            option.simpleVersions.sort((e1: OptionVersionSimple, e2: OptionVersionSimple) => e1.order - e2.order);
          }
        });
        data.sort((a: OptionInfo, b: OptionInfo) => a.title.localeCompare(b.title));
      })
    );
  }

  //#endregion

  //#region common utils
  public convertOptionToViewModel(option: OptionInfo): GridOption {
    const gridOption: GridOption = { ...option, files: [], items: [], versionCache: {} };
    if (option?.simpleVersions?.length) {
      gridOption.currentStatus = option.simpleVersions[0].status;
      gridOption.currentVersion = option.simpleVersions[0].versionNumber;
      gridOption.activeVersion = option.simpleVersions[0];
    }
    if (option.thumbnails) {
      const files: any[] = [];
      Object.entries(option.thumbnails).forEach((el: [string, OptionThumbnail]) => {
        const [, { id, contentType, url }] = el;
        const displayName: string = url.split('/').pop();
        files.push({
          displayName,
          fileName: displayName,
          signedURL: url,
          contentType,
          malwareScanningStatus: MalwareScanningStatus.Clean,
          id,
        });
      });

      gridOption.files = files;
      gridOption.items = files;
    }
    return gridOption;
  }

  //#endregion

  public getVersion(args: GetVersionFilesParams): Observable<OptionVersion> {
    const url: string =
      `/api/projects/${args.projectId}/sections/${args.sectionId}/subsections/${args.subsectionId}/` +
      `v-versioning/${args.itemId}/${args.versionNumber}`;
    return this.apiService.httpGet(url);
  }

  public sortFilesByFileOrder(version: OptionVersion): void {
    if (version.files) {
      version.files.sort((a: FileInfo, b: FileInfo) => (a.displayName || '').localeCompare(b.displayName || ''));
    }
  }

  //#region subsection functions

  public updateSubsectionState(state: Partial<ActiveSubsectionState>): void {
    this.subsectionState = { ...this.subsectionState, ...state };
    this.subsection$.next(this.subsectionState);
  }

  public clearSubsectionState(): void {
    this.subsection$.next(null);
  }

  public updateUIState(state: Partial<ProjectSubSectionUIState>): void {
    const next = { ...this.uiState, ...state };
    this.uiState = next;
    this.uiState$.next(this.uiState);
  }

  /**
   * Seeds `explorerCollapsed` from the `tv` query param, for callers that run
   * before the explorer's first render.
   *
   * `TopNavComponent` also applies `tv`, but it does so from a debounced stream
   * that only fires after the page is up — late enough that `?tv=0` mounts
   * `<subsection-explorer>` at its expanded default and then collapses it, which
   * reads as the tree flashing in. Applying it here means that state is never
   * rendered. Both paths write the same value, so the later one is a no-op.
   *
   * No-ops when the param is absent or unrecognised, leaving the default.
   */
  public applyTreeViewQueryParam(): void {
    const treeView: boolean | null = this.viewContext.read(VIEW_CONTEXT_KEY.treeView);
    if (treeView === null) {
      return;
    }
    this.updateUIState({ explorerCollapsed: !treeView });
  }

  public ensureCommentsPanelOpenOnFirstLoad(): void {
    if (this.hasInitializedCommentsPanelState) {
      return;
    }
    this.hasInitializedCommentsPanelState = true;
    this.updateUIState({ commentsCollapsed: false });
  }

  public updateOptionsState(state: QueryState<OptionInfo>): void {
    this.options$.next(state);
  }

  public updateTopicsState(state: QueryState<TopicInfo>): void {
    this.topics$.next(state);
  }

  //#endregion

  //#region option functions

  public getOption(
    projectId: string,
    sectionId: string,
    subsectionId: string,
    optionId: string
  ): Observable<OptionInfo> {
    const url: string = `/api/versioning/options/${optionId}?project=${projectId}&section=${sectionId}&subsection=${subsectionId}&optionId=${optionId}`;
    return this.apiService.httpGet(url);
  }

  public createOption(option: Partial<OptionInfo>): Observable<OptionUpdateResponse> {
    const url: string = `/api/versioning/options`;
    return this.apiService.httpPost(url, option);
  }

  public updateOption(option: Partial<OptionInfo>): Observable<OptionUpdateResponse> {
    const url: string = `/api/versioning/options`;
    return this.apiService.httpPut(url, option);
  }

  public deleteOption(projectId: string, sectionId: string, subsectionId: string, optionId: string): Observable<any> {
    const url: string = `/api/versioning/options?project=${projectId}&section=${sectionId}&subsection=${subsectionId}&optionId=${optionId}`;
    return this.apiService.httpDelete(url);
  }

  //#endregion

  //#region page

  public getFilePages(fileId: string): Observable<FilePages> {
    const url: string = `/api/file-pages/${fileId}/pages`;
    return this.apiService.httpGet(url);
  }

  //#endregion

  private buildQueryParams(params: SubsectionNavigationParams, additionalParams: Record<string, any> = {}): Params {
    const queryParams: Params = { ...additionalParams };

    if (params.topicId) {
      queryParams.topicId = params.topicId;
    }
    if (params.optionId) {
      queryParams.optionId = params.optionId;
    }
    if (params.versionNumber) {
      queryParams.version = params.versionNumber;
    }
    if (params.fileId) {
      queryParams.fileId = params.fileId;
    }
    if (params.page) {
      queryParams.page = params.page;
    }

    return queryParams;
  }

  private buildSubsectionUrl(params: SubsectionNavigationParams): { path: string[]; queryParams: Params } {
    // Drive mode: the explorer navigates by Drive directoryUuid via ?dirId — not legacy
    // path segments. Omitting legacy query params clears any stale option/version/file
    // selection; the explorer manages ?fileId itself.
    if (this.isDriveMode()) {
      return {
        path: [`/projects/${params.projectId}`],
        queryParams: {
          ...this.viewContext.getPersistedParams(),
          ...(params.driveDirectoryUuid ? { dirId: params.driveDirectoryUuid } : {}),
        },
      };
    }

    let basePath: string = `/projects/${params.projectId}`;
    const projectPath: string = basePath;
    if (params.sectionId) {
      basePath += `/${params.sectionId}`;
    }
    if (params.subsectionId) {
      basePath += `/${params.subsectionId}`;
    }

    const urlConfigs: Record<string, SubsectionUrlConfig> = {
      release: {
        condition: () => params.navigationType === 'release',
        path: () => (params.releaseId ? `${basePath}/releases/${params.releaseId}` : `${basePath}/releases`),
        queryParams: () => ({ releaseAssetId: params.releaseAssetId, releaseFileId: params.releaseFileId }),
      },
      shareLink: {
        condition: () => params.navigationType === 'share-link',
        path: () => (params.shareLinkId ? `${basePath}/share-links/${params.shareLinkId}` : `${basePath}/share-links`),
        queryParams: () => this.buildQueryParams(params, { shareAssetId: params.shareAssetId }),
      },
      kpf: {
        condition: () => !!params.folderId,
        path: () => `${projectPath}/${params.fileId ? 'kpf-preview' : 'kpf'}`,
        queryParams: () => this.buildQueryParams(params, { folderId: params.folderId }),
      },
      preview: {
        condition: () => typeof params.versionNumber === 'number',
        path: () => `${basePath}/${params.fileId ? 'preview' : 'files'}`,
        queryParams: () => this.buildQueryParams(params),
      },
      grid: {
        condition: () => !!params.topicId,
        path: () => `${basePath}/grid`,
        queryParams: () => this.buildQueryParams(params),
      },
      default: {
        condition: () => true,
        path: () => basePath,
        queryParams: () => ({}),
      },
    };

    // Find the first matching configuration
    const config: SubsectionUrlConfig = Object.values(urlConfigs).find((config: SubsectionUrlConfig) =>
      config.condition()
    );

    // The context handle (`ctx`) is page state, not navigation state — carry
    // it across, since every branch above rebuilds the query string from scratch.
    return {
      path: [config.path()],
      queryParams: { ...this.viewContext.getPersistedParams(), ...config.queryParams() },
    };
  }

  public getSubsectionUrl(params: SubsectionNavigationParams): string {
    const { path, queryParams } = this.buildSubsectionUrl(params);
    const queryString: string = new URLSearchParams(queryParams).toString();
    return queryString ? `${path[0]}?${queryString}` : path[0];
  }

  public navigateSubsection(params: SubsectionNavigationParams): void {
    const { path, queryParams } = this.buildSubsectionUrl(params);

    const targetUrlTree = this.router.createUrlTree(path, { queryParams });

    if (!this.router.isActive(targetUrlTree, true)) {
      this.router.navigate(path, { queryParams });
    }

    if (typeof params.versionNumber === 'number') {
      this.lastSubsectionParams[params.subsectionId] = params;
    }
  }

  public navigateReviewLink(params: ReviewLinkNavigationParams): void {
    const previewParams: Params = {
      topicId: params.topicId,
      optionId: params.optionId,
      version: params.versionNumber,
      fileId: params.fileId,
    };
    this.router.navigate([`/review/${params.id}`], {
      queryParams: previewParams,
    });
  }

  public getLatestSubsectionParams(subsectionId: string): SubsectionNavigationParams | null {
    if (subsectionId in this.lastSubsectionParams) {
      return this.lastSubsectionParams[subsectionId];
    }

    return null;
  }
}
